import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { IRecurringBookingState } from './types';
import {
  IRecurringBookingRequest,
  IRecurringBookingSaveRequest,
  IRecurringBookingSubset,
  IRecurringBookingDetail,
} from 'external-services/recurring-booking-service/type';
import { isEmpty } from 'lodash-es';
import RecurringBookingService from 'external-services/recurring-booking-service';
import { DiaryRootState } from 'appRoot/rootReducer';
import { IBooking } from 'shared-types/SharedTypes';
import { displayActions } from 'booking-creator/reducers/display/slice';
import { orderBy } from 'lodash-es';
import { BookingMode } from 'booking-creator/reducers/display/types';
import {
  updateCanSaveEditBooking,
  updateCanSaveNewBooking,
} from 'booking-creator/reducers/validation/slice';
import { RecurringBookingConfirmStatus } from 'booking-creator/reducers/recurringBooking/types';

const NS = 'recurringBookingSlice';

export function createRecurringBookingID() {
  return `recurringbooking_${Date.now()}`;
}

export const saveRecurringBookings = createAsyncThunk(
  'recurringBooking/saveRecurringBookings',
  async (
    payload: IRecurringBookingSaveRequest,
    { dispatch, getState }
  ): Promise<{
    requestParameters: IRecurringBookingRequest;
    bookingList: IRecurringBookingSubset[];
  }> => {
    const { displayReducer } = getState() as DiaryRootState;

    const bookingList = payload.bookingList.map((item) => item.booking);
    const activeBooking = displayReducer.activeBooking;
    const mode = displayReducer.mode;
    let requestParameters: IRecurringBookingRequest = null;
    if (!isEmpty(bookingList)) {
      console.log(NS, 'saveRecurringBookings payload===>', payload);
      requestParameters = RecurringBookingService.MakeRecurringBookingRequestForSaveBooking(
        payload,
        activeBooking as IBooking
      );
      requestParameters.bookings = bookingList;

      const recurringBookingId = createRecurringBookingID();
      requestParameters.recurringBooking.id = recurringBookingId;
      dispatch(recurringBookingSlice.actions.changeStatusConfirmation(RecurringBookingConfirmStatus.Confirmed))
      dispatch(displayActions.setRecurringBookingId(recurringBookingId));

      // To remove error message after clicking DONE
      setTimeout(() => {
        if (mode === BookingMode.newBooking) dispatch(updateCanSaveNewBooking());
        else if (mode === BookingMode.editBooking) dispatch(updateCanSaveEditBooking());
      }, 100)
    } else {
      dispatch(displayActions.setRecurringBookingId(null));
    }

    return { requestParameters, bookingList };
  }
);

export const loadRecurringBookings = createAsyncThunk(
  'recurringBooking/loadRecurringBookings',
  async (payload: {booking: IBooking}, { dispatch, getState }): Promise<IRecurringBookingSubset[]> => {
    const {
      displayReducer: { activeBooking, activeSchedule },
      scheduleReducer: { selectedDate, selectedSchedule },
      bookingReducer: { couchReducedBookings },
    } = getState() as DiaryRootState;
    const bookingId: string = payload.booking.recurringBookingId;
    try {
      if (bookingId) {
        const result: IRecurringBookingDetail = await RecurringBookingService.getRecurringBookings(bookingId);
        const bookings: IRecurringBookingSubset[] = orderBy(result.bookings, 'time');
        return  bookings ? bookings : [];
      } else {
        return [];
      }
    } catch (err) {
      console.warn(
        NS,
        `Error on 'loadRecurringBookings' getting bookings. booking: `,
        payload.booking,
        err
      );
      return [];
    }
  }
);

const initialState: IRecurringBookingState = {
  isRecurringBookingSaved: false,
  isClickedLoad: false,
  openRepeatBookingTab: false,
  requestParameters: null,
  bookingList: [], //This is for new booking
  recurringBookingList: [], // This is for edit booking
  recurringBookingConfirmationStatus: RecurringBookingConfirmStatus.NotStartConfirming,
};

const recurringBookingSlice = createSlice({
  name: 'recurringBooking', // used in action types
  initialState,
  reducers: {
    reset: () => {
      return initialState;
    },
    setIsRecurringBookingSaved: (state: IRecurringBookingState, action: PayloadAction<boolean>) => {
      state.isRecurringBookingSaved = action.payload;
    },
    setIsClickedLoad: (state: IRecurringBookingState, action: PayloadAction<boolean>) => {
      state.isClickedLoad = action.payload;
    },
    setOpenRepeatBookingTab: (state: IRecurringBookingState, action: PayloadAction<boolean>) => {
      state.openRepeatBookingTab = action.payload;
    },
    changeStatusConfirmation: (state: IRecurringBookingState, action: PayloadAction<RecurringBookingConfirmStatus>) => {
      state.recurringBookingConfirmationStatus = action.payload
    } 
  },

  extraReducers: (builder) => {
    builder.addCase(saveRecurringBookings.fulfilled, (state: IRecurringBookingState, action) => {
      state.requestParameters = action.payload.requestParameters;
      state.bookingList = action.payload.bookingList;
    });
    builder.addCase(loadRecurringBookings.fulfilled, (state: IRecurringBookingState, action) => {
      state.recurringBookingList = action.payload;
    });
  },
});

// Extract the action creators object and the reducer
const { actions, reducer } = recurringBookingSlice;
// Extract and export each action creator by name
export { actions as recurringBookingActions };
// Export the reducer, either as a default or named export
export default reducer;
